import {
    faArrowPointer,
    faCube,
    faEllipsis,
    faMicrochipAi,
    faMusic,
    faPhotoFilmMusic,
    faVideo,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { CompGroup } from "userful-chronos-app-common-js/dist/models/group/Groups";
import {
    BetaSources,
    getSamSourceIconPath,
    isSourceInteractive,
    SamSourceDef,
} from "userful-chronos-app-common-js/dist/models/sam/SAMSource";
import Card from "../Card";
import CardHeader from "../header/CardHeader";
import CardHeaderList from "../header/CardHeaderList";
import Tag from "../../tag/Tag";
import SourceActionsMenu from "../../dropdowns/SourceActionsMenu";
import Button from "../../button/Button";
import CardBody from "../body/CardBody";
import { BetaTag } from "../../Widgets/BetaTag/BetaTag";
import { useTranslation } from "react-i18next";
import UserfulTooltip from "../../Widgets/UserfulTooltip";
import { MediaSupportType } from "userful-chronos-app-common-js/dist/models/sam/Common";

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
    source: SamSourceDef;
    parent?: CompGroup;
    onMoveOut?: () => void;
    onEdit?: (source: SamSourceDef) => void;
}

export default function SourceCard({ source, parent, onMoveOut, onEdit, ...rest }: IProps) {
    const { t } = useTranslation();

    const { children: _children, ...htmlProps } = rest;

    const sourceOutput = source.outputType;
    const sharingMode = source.sharingMode;
    const isAIPowered = false;
    const isInteractive = isSourceInteractive(source.type.value);
    const backgroundImg = getSamSourceIconPath(source, "images/Dark-Salmon.jpg");

    const debug = false;

    return (
        <Card {...htmlProps}>
            <CardHeader>
                <CardHeaderList>
                    {(debug || sharingMode !== "UNIQUE_SOURCE") && (
                        <UserfulTooltip placement="top" title={t("Decisions.SDL.Type.sharedSource")}>
                            <Tag iconOnly variant={"source-light-blue"}>
                                <FontAwesomeIcon icon={faCube} />
                            </Tag>
                        </UserfulTooltip>
                    )}
                    {(debug || sharingMode === "UNIQUE_SOURCE") && (
                        <UserfulTooltip placement="top" title={t("Decisions.SDL.Type.unsharedSource")}>
                            <Tag iconOnly variant={"source-dark-blue"}>
                                <FontAwesomeIcon icon={faCube} />
                            </Tag>
                        </UserfulTooltip>
                    )}

                    {(debug || isInteractive) && (
                        <UserfulTooltip placement="top" title={t("Decisions.SDL.Type.interactive")}>
                            <Tag iconOnly variant={"green"}>
                                <FontAwesomeIcon icon={faArrowPointer} />
                            </Tag>
                        </UserfulTooltip>
                    )}

                    {(debug || isAIPowered) && (
                        <UserfulTooltip placement="top" title={t("Decisions.SDL.Type.interactive")}>
                            <Tag iconOnly variant={"purple"}>
                                <FontAwesomeIcon icon={faMicrochipAi} />
                            </Tag>
                        </UserfulTooltip>
                    )}

                    {(debug || sourceOutput === "VIDEO_ONLY") && (
                        <UserfulTooltip placement="top" title={t("Decisions.SDL.Type.videoOnly")}>
                            <Tag iconOnly variant={"grey"}>
                                <FontAwesomeIcon icon={faVideo} />
                            </Tag>
                        </UserfulTooltip>
                    )}
                    {(debug || sourceOutput === "VIDEO_AND_AUDIO") && (
                        <UserfulTooltip placement="top" title={t("Decisions.SDL.Type.videoAudio")}>
                            <Tag iconOnly variant={"grey"}>
                                <FontAwesomeIcon icon={faPhotoFilmMusic} />
                            </Tag>
                        </UserfulTooltip>
                    )}
                    {(debug || sourceOutput === "AUDIO_ONLY") && (
                        <UserfulTooltip placement="top" title={t("Decisions.SDL.Type.audioOnly")}>
                            <Tag iconOnly variant={"grey"}>
                                <FontAwesomeIcon icon={faMusic} />
                            </Tag>
                        </UserfulTooltip>
                    )}
                </CardHeaderList>
                <SourceActionsMenu
                    value={source}
                    parent={parent}
                    trigger={
                        <Button variant={"ghost"} iconOnly style={{ borderLeft: "1px solid #c6c8ce", borderRadius: 0 }}>
                            <FontAwesomeIcon icon={faEllipsis} />
                        </Button>
                    }
                    onMoveOut={onMoveOut ?? (() => {})}
                    onEdit={onEdit ?? (() => {})}
                />
            </CardHeader>
            <CardHeader>{source.name}</CardHeader>
            <CardBody containsImage>
                <img src={backgroundImg} alt="sourceBackgroundImage" draggable={false} />
                {BetaSources.includes(source.type.value) && (
                    <div className="absoluteBeta">
                        <BetaTag />
                    </div>
                )}
            </CardBody>
        </Card>
    );
}
